/**
 * Frontend Logging Service
 * 
 * Provides structured logging for the frontend application.
 * Follows international standards and project rules (.cursorrules).
 * 
 * Logging levels:
 * - DEBUG: Detailed information for debugging (only in development)
 * - INFO: General informational messages
 * - WARN: Warning messages for potential issues
 * - ERROR: Error messages for failures
 */

type LogLevel = "DEBUG" | "INFO" | "WARN" | "ERROR";

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
  error?: Error;
}

class Logger {
  private readonly isDevelopment: boolean;
  private readonly logHistory: LogEntry[] = [];
  private readonly maxHistorySize = 100;

  constructor() {
    this.isDevelopment = import.meta.env.DEV || import.meta.env.MODE === "development";
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>,
    error?: Error
  ): LogEntry {
    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      context,
      error: error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : undefined,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (level === "DEBUG" && !this.isDevelopment) {
      return false;
    }
    return true;
  }

  private addToHistory(entry: LogEntry): void {
    this.logHistory.push(entry);
    if (this.logHistory.length > this.maxHistorySize) {
      this.logHistory.shift();
    }
  }

  private formatMessage(entry: LogEntry): string {
    const contextStr = entry.context ? ` ${JSON.stringify(entry.context)}` : "";
    const errorStr = entry.error ? ` Error: ${entry.error.message}` : "";
    return `[${entry.level}] ${entry.message}${contextStr}${errorStr}`;
  }

  private log(level: LogLevel, message: string, context?: Record<string, unknown>, error?: Error): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const entry = this.createLogEntry(level, message, context, error);
    this.addToHistory(entry);

    if (this.isDevelopment) {
      const formattedMessage = this.formatMessage(entry);
      switch (level) {
        case "DEBUG":
          // eslint-disable-next-line no-console
          console.debug(formattedMessage);
          break;
        case "INFO":
          // eslint-disable-next-line no-console
          console.info(formattedMessage);
          break;
        case "WARN":
          // eslint-disable-next-line no-console
          console.warn(formattedMessage);
          break;
        case "ERROR":
          // eslint-disable-next-line no-console
          console.error(formattedMessage, error);
          break;
      }
    } else {
      if (level === "ERROR") {
        this.sendToErrorTracking(entry);
      }
    }
  }

  private sendToErrorTracking(entry: LogEntry): void {
    try {
      if (typeof window !== "undefined" && window.navigator?.sendBeacon) {
        const errorData = {
          level: entry.level,
          message: entry.message,
          timestamp: entry.timestamp,
          context: entry.context,
          error: entry.error,
          userAgent: navigator.userAgent,
          url: window.location.href,
        };
        const blob = new Blob([JSON.stringify(errorData)], { type: "application/json" });
        window.navigator.sendBeacon("/api/logs/error", blob);
      }
    } catch {
      // Silently fail if error tracking is unavailable
    }
  }

  debug(message: string, context?: Record<string, unknown>): void {
    this.log("DEBUG", message, context);
  }

  info(message: string, context?: Record<string, unknown>): void {
    this.log("INFO", message, context);
  }

  warn(message: string, context?: Record<string, unknown>): void {
    this.log("WARN", message, context);
  }

  error(message: string, error?: Error, context?: Record<string, unknown>): void {
    this.log("ERROR", message, context, error);
  }

  getHistory(): readonly LogEntry[] {
    return [...this.logHistory];
  }

  clearHistory(): void {
    this.logHistory.length = 0;
  }
}

export const logger = new Logger();

